import { notification } from "antd";
import { postAPI } from "../../utils/api";
import { CONFIRM_PAYMENT_URL } from "../../utils/constants";
import { extractApiErrorMessage } from "../../utils/helpers";

export interface PaymentHandlerParams {
  paymentData: any;
  initiatePayload: any;
  handlePaymentStatus: (status: number) => void;
  setPaymentLoading: (loading: boolean) => void;
  setIsPaymentFailed: (failed: boolean) => void;
  setPaymentErrorMessage: (msg: string) => void;
  loadPaystackScript: () => Promise<boolean>;
  onMpesaInitiated?: (paymentData: any, initiatePayload: any) => void;
  setLastVerifyPayload: (payload: any) => void;
  onVerificationStarted?: (verifyPayload: any) => void;
  onVerificationEnded?: () => void;
  publicKey?: string;
}

export type PaymentHandler = (params: PaymentHandlerParams) => Promise<void>;

export const handleCardPayment: PaymentHandler = async ({
  paymentData,
  initiatePayload,
  handlePaymentStatus,
  setPaymentLoading,
  setIsPaymentFailed,
  setPaymentErrorMessage,
  loadPaystackScript,
  setLastVerifyPayload,
  onVerificationStarted,
  onVerificationEnded,
  publicKey,
}) => {
  const accessCode = paymentData?.AccessCode;

  if (!accessCode) {
    setPaymentLoading(false);
    notification.error({
      message: "Payment Failed",
      description: "Payment token could not be retrieved. Please try again.",
      placement: "topRight",
    });
    return;
  }

  const loaded = await loadPaystackScript();
  if (!loaded) {
    setPaymentLoading(false);
    notification.error({
      message: "Payment Failed",
      description: "Failed to load Paystack SDK. Please try again.",
      placement: "topRight",
    });
    return;
  }

  const paystackKey = publicKey || (process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_PAYSTACK_PUBLIC_KEY
    : (window as any).env?.paystackPublicKey);

  const paystack = new (window as any).PaystackPop();
  paystack.newTransaction({
    key: paystackKey,
    accessCode: accessCode,
    onSuccess: async (transaction: any) => {
      setPaymentLoading(true);
      const verifyPayload = {
        ...initiatePayload,
        reference: transaction.reference,
      };
      setLastVerifyPayload(verifyPayload);
      if (onVerificationStarted) {
        onVerificationStarted(verifyPayload);
      }

      const verifyRes = await postAPI(CONFIRM_PAYMENT_URL, verifyPayload);

      if (verifyRes.status === 200 && verifyRes.data?.IsSuccessful) {
        const verifyData = verifyRes.data.Data;
        const paymentStatus = verifyData?.PaymentStatus;
        handlePaymentStatus(paymentStatus);
      } else {
        setPaymentLoading(false);
        setIsPaymentFailed(true);
        setPaymentErrorMessage("Sorry, we encountered an issue while trying to process your payment. Kindly ensure your account is properly funded and try again");
        if (onVerificationEnded) {
          onVerificationEnded();
        }
      }
    },
    onCancel: async () => {
      setPaymentLoading(true);
      const verifyPayload = {
        ...initiatePayload,
      };
      setLastVerifyPayload(verifyPayload);
      if (onVerificationStarted) {
        onVerificationStarted(verifyPayload);
      }
      try {
        const verifyRes = await postAPI(CONFIRM_PAYMENT_URL, verifyPayload);
        if (verifyRes.status === 200 && verifyRes.data?.IsSuccessful) {
          const verifyData = verifyRes.data.Data;
          const paymentStatus = verifyData?.PaymentStatus;
          handlePaymentStatus(paymentStatus);
          return;
        }
      } catch (err) {
        console.error("Error verifying payment on cancel:", err);
      } finally {
        setPaymentLoading(false);
        if (onVerificationEnded) {
          onVerificationEnded();
        }
      }

      notification.warning({
        message: "Payment Cancelled",
        description: "Payment interface was closed.",
        placement: "topRight",
      });
    },
  });
};

export const handleMpesaPayment: PaymentHandler = async ({
  paymentData,
  initiatePayload,
  setPaymentLoading,
  onMpesaInitiated,
}) => {
  setPaymentLoading(false);
  if (onMpesaInitiated) {
    onMpesaInitiated(paymentData, initiatePayload);
  }
};

export const handleWalletPayment: PaymentHandler = async ({
  paymentData,
  initiatePayload,
  handlePaymentStatus,
  setPaymentLoading,
  setIsPaymentFailed,
  setPaymentErrorMessage,
  setLastVerifyPayload,
  onVerificationStarted,
  onVerificationEnded,
}) => {
  const reference = paymentData?.TransactionReference || paymentData?.Reference || "";
  const verifyPayload = {
    ...initiatePayload,
    reference,
  };

  setLastVerifyPayload(verifyPayload);

  setPaymentLoading(true);
  if (onVerificationStarted) {
    onVerificationStarted(verifyPayload);
  }

  try {
    const verifyRes = await postAPI(CONFIRM_PAYMENT_URL, verifyPayload);
    if (verifyRes.status === 200 && verifyRes.data?.IsSuccessful) {
      const verifyData = verifyRes.data.Data;
      const paymentStatus = verifyData?.PaymentStatus;
      handlePaymentStatus(paymentStatus);
    } else {
      setPaymentLoading(false);
      setIsPaymentFailed(true);
      const errorMsg = extractApiErrorMessage(verifyRes.data, "Payment confirmation failed.");
      setPaymentErrorMessage(errorMsg);
      if (onVerificationEnded) {
        onVerificationEnded();
      }
    }
  } catch (err: any) {
    console.error("Wallet payment confirmation error:", err);
    setPaymentLoading(false);
    setIsPaymentFailed(true);
    const errorMsg = extractApiErrorMessage(err.response?.data, "Sorry, we encountered an issue while trying to process your payment.");
    setPaymentErrorMessage(errorMsg);
    if (onVerificationEnded) {
      onVerificationEnded();
    }
  }
};

export const PaymentHandlerRegistry: Record<string, PaymentHandler> = {
  card: handleCardPayment,
  mpesa: handleMpesaPayment,
  wallet: handleWalletPayment,
};
